import { PortAndWaterwayObject, RemoteOrganizer } from '../types';
import { SelectedKrets } from '../catalog/krets-actions';
import $ from 'jquery';

export const $html = $('html');
export const $body = $(document.body);
export const MFP_FORCE_SCROLL = 'mfp-force-scroll';

export function stickyKitRecalc(): void {
  $body.trigger('sticky_kit:recalc');
}

export function getQueryParameter(variable: string): string | null {
  const query = window.location.search.substring(1);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=');
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return null;
}

export function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function oneYear(): Date {
  const d = today();
  return new Date(d.getFullYear() + 1, d.getMonth() + 1, 0);
}

export function formatDate(date: Date): string {
  const tzo = -date.getTimezoneOffset();
  const dif = tzo >= 0 ? '+' : '-';
  const pad = function pad(num): string {
    const norm = Math.floor(Math.abs(num));
    return (norm < 10 ? '0' : '') + norm;
  };
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds()) +
    dif +
    pad(tzo / 60) +
    ':' +
    pad(tzo % 60)
  );
}

export function toDateString(date: Date | string): string {
  if (date instanceof Date) {
    return formatDate(date).substring(0, 10);
  }
  return date.substring(0, 10);
}

function createReq(resolve, reject): XMLHttpRequest {
  const req = new XMLHttpRequest();
  req.addEventListener('load', (): void => {
    if (req.status > 199 && req.status < 400) {
      resolve(JSON.parse(req.responseText));
    } else {
      reject(new Error(req.statusText));
    }
  });
  req.addEventListener('abort', (): void => {
    resolve(null);
  });
  req.addEventListener('error', (evt): void => {
    reject(evt);
  });
  return req;
}

export function get<T>(uri: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const req = createReq(resolve, reject);
    req.open('GET', uri);
    req.setRequestHeader('Accept', 'application/json');
    req.send();
  });
}

export function post<T>(uri: string, data: FormData | string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const req = createReq(resolve, reject);
    req.open('POST', uri);
    req.setRequestHeader('Accept', 'application/json');
    if (!(data instanceof FormData)) {
      req.setRequestHeader('Content-Type', 'application/json');
    }
    req.send(data);
  });
}

export function parseValues(value: string | null, organizers: RemoteOrganizer[] = []): SelectedKrets[] {
  if (value) {
    return value
      .replace(/([0-9,]+)/, '$1')
      .split(',')
      .map((k) => {
        const id = parseInt(k, 10);
        const org = organizers.find((organizer) => organizer.id === id);
        return {
          value: id,
          label: (org && org.name) || '',
        };
      });
  }
  return [];
}

export function decorateOrganizer(selected: SelectedKrets[], organizers: RemoteOrganizer[] = []): SelectedKrets[] {
  for (let i = 0; i < selected.length; i++) {
    const item = selected[i];
    if (!item.label) {
      const org = organizers.find((organizer) => organizer.id === item.value);
      if (org) {
        item.label = org.name;
      }
    }
  }
  return selected;
}

/**
 * Hamburger item toggle
 * HTML structure:
 *  <.hb-container>
 *    <random>
 *      <.hb-toggle-trigger> {% include '@sxk_theme/includes/hb-toggle.html.twig' %}
 *    <.hb-item-content>
 *      <.hb-item-content-inner>
 *
 * Used in: Forums, Chart, all #sidebar-first blocks @ < $mobile-width viewport width
 *
 * @param that - Clicked element
 */
export function hbToggle(that: JQuery): void {
  const container = that.closest('.hb-container');
  const targetContent = container.children('.hb-item-content');

  if (container.hasClass('closed')) {
    const contentHeight = targetContent.children('.hb-item-content-inner').outerHeight();
    targetContent.stop().animate({ height: contentHeight }, 500, function openAnimationComplete() {
      targetContent.removeAttr('style');
    });
  } else {
    targetContent.stop().animate({ height: 0 }, 500, function closeAnimationComplete() {
      targetContent.removeAttr('style');
    });
  }
  container.toggleClass('open closed');
}

export function initMainImagePopup(item: string | JQuery, src: string): void {
  const $item = typeof item === 'string' ? $(item) : item;
  $item.magnificPopup({
    type: 'image',
    items: {
      src,
    },
    // Delay in milliseconds before popup is removed
    removalDelay: 300,
    // Animation class added to popup wrapper and background
    mainClass: 'mfp-fade',
    preloader: true,
    callbacks: {
      open: function open(): void {
        $html.addClass(MFP_FORCE_SCROLL);
      },
      close: function close(): void {
        $html.removeClass(MFP_FORCE_SCROLL);
      },
    },
  });
}

export const portAndWaterwayObjectSorter =
  (sortGeo: SortDirection) =>
  (a: PortAndWaterwayObject, b: PortAndWaterwayObject): number => {
    let sort = 0;
    if (sortGeo === SortDirection.ASCENDING) {
      sort = b.latitude - a.latitude;
    } else if (sortGeo === SortDirection.DESCENDING) {
      sort = a.latitude - b.latitude;
    }
    return sort;
  };

export const COORD_PATTERN = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)(,[0-9]{1,2})?$/;
export const NODE_ID_PATTERN = /^N[0-9]+$/;
export const MEDLEM_ID_PATTERN = /^M[0-9]+$/;
export enum SortDirection {
  ASCENDING,
  DESCENDING,
  NONE,
}
export const COLLATOR = new Intl.Collator('sv');
export const kretsSorter = (a: RemoteOrganizer, b: RemoteOrganizer): number => {
  if (a.type === 'Riksförening') {
    return -1;
  } else if (b.type === 'Riksförening') {
    return 1;
  } else if (a === b) {
    return 0;
  }
  return COLLATOR.compare(a.name, b.name);
};
export const BUOY_TYPES = ['klassisk_boj', 'buoy'];

export function striptags(content: string): string {
  const frag = document.createDocumentFragment();
  const innerEl = document.createElement('div');
  frag.appendChild(innerEl);
  innerEl.innerHTML = content;
  return frag.firstChild.textContent;
}
